import {
  HttpEvent,
  HttpEventType,
  HttpProgressEvent,
  HttpResponse
} from '@angular/common/http';
import { distinctUntilChanged, Observable, scan } from 'rxjs';
import { Download } from './models/general.models';
import { Layer } from './models/layer.models';

export function getColormapParams(
  layer: Layer,
  excludeRescale?: boolean,
  useAdditional?: boolean
) {
  if (!layer) {
    return '';
  }
  let colormap_type: string;
  let colormap_scale: string;
  if (layer.layer.value_type === 'CONTINUOUS') {
    const keys = useAdditional
      ? Object.keys(layer.layer.colormap_values)
      : Object.keys(layer.layer.colormap_values);
    colormap_type = 'linear';
    colormap_scale = `0,${keys[keys.length - 1]}`;
  } else {
    colormap_type = 'explicit';
    colormap_scale = '0,100';
  }
  // if (layer.rescale) {
  //   colormap_scale = `${layer.rescale.from},${layer.rescale.to}`;
  // }

  // Convert the colormap object to a JSON string
  const colormapJson = useAdditional
    ? JSON.stringify(layer.layer.colormap_values)
    : JSON.stringify(layer.layer.colormap_values);
  let result = `&colormap=${encodeURIComponent(
    colormapJson
  )}&colormap_type=${colormap_type}`;
  if (!excludeRescale) {
    result += `&rescale=${colormap_scale}`;
  }
  return result;
}

export function isDefinedAndNotNull(value: any): boolean {
  return typeof value !== 'undefined' && value !== null;
}

export function getRiskColors(): string[] {
  return ['#B7B6B4', '#F8E18C', '#F19C5C', '#D95A5F', '#61120D'];
}

export function compareArrays(array1: Array<any>, array2: Array<any>) {
  return (
    array1.length === array2.length &&
    array1.every((value, index) => value === array2[index])
  );
}

export function findFirstNonEmptyString(obj: any): string | null {
  function isNonEmptyString(value: any): value is string {
    return typeof value === 'string' && value.trim() !== '';
  }
  function traverse(value: any): string | null {
    if (isNonEmptyString(value)) {
      return value;
    } else if (Array.isArray(value)) {
      for (const item of value) {
        const result = traverse(item);
        if (result) return result;
      }
    } else if (typeof value === 'object' && value !== null) {
      for (const key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          const result = traverse(value[key]);
          if (result) return result;
        }
      }
    }
    return null;
  }
  return traverse(obj);
}

function isHttpResponse<T>(event: HttpEvent<T>): event is HttpResponse<T> {
  return event.type === HttpEventType.Response;
}

function isHttpProgressEvent(
  event: HttpEvent<unknown>
): event is HttpProgressEvent {
  return (
    event.type === HttpEventType.DownloadProgress ||
    event.type === HttpEventType.UploadProgress
  );
}
export function download(
  saver?: (b: Blob) => void
): (source: Observable<HttpEvent<Blob>>) => Observable<Download> {
  return (source: Observable<HttpEvent<Blob>>) =>
    source.pipe(
      scan(
        (download: Download, event): Download => {
          if (isHttpProgressEvent(event)) {
            return {
              total: event.total,
              loaded: event.loaded,
              progress: event.total
                ? Math.round((100 * event.loaded) / event.total)
                : +download.progress,
              state: 'IN_PROGRESS',
              content: null
            };
          }
          if (isHttpResponse(event)) {
            if (saver) {
              saver(event.body);
            }
            return {
              total: event.body.size,
              loaded: event.body.size,
              progress: 100,
              state: 'DONE',
              content: event.body
            };
          }
          return download;
        },
        { state: 'PENDING', progress: 0, content: null, loaded: 0, total: 0 }
      ),
      distinctUntilChanged(
        (a, b) =>
          a.state === b.state &&
          a.progress === b.progress &&
          a.content === b.content
      )
    );
}

export function getGradientBackground(layer: Layer): string {
  const colorMap = layer.layer.colormap_values;
  const max = Math.max(...Object.keys(colorMap).map((el) => +el));
  const colors = Object.entries(colorMap)
    .map(([key, value]) => {
      const percent = (+key / max) * 100;
      return `${value} ${percent}%`;
    })
    .join(', ');
  return `linear-gradient(to right, ${colors})`;
}

export function getDiscreteColors(layer: Layer): string[] {
  return Object.values(layer.layer.colormap_values);
}

export function isNumeric(value: string): boolean {
  return !isNaN(parseFloat(value));
}

export function formatLegend(legend: string, name?: string): string {
  const value: number = parseFloat(legend);
  if (name === 'pcba' && value === 0) {
    return 'Deciduous (0%)';
  } else if (name === 'pcba' && value === 100) {
    return 'Conifer (100%)';
  } else if (name === 'pcba' && value === 50) {
    return '';
  }
  if (!isNaN(value)) {
    return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(0);
  }
  return legend;
}

export function getSpacedNumericLegends(legends: Layer): string[] {
  const numericLegends = Object.keys(legends.layer.colormap_values);
  const min = legends.layer.min_threshold;
  const max = legends.layer.max_threshold;
  const step = (max - min) / (numericLegends.length - 1);

  return Array.from({ length: numericLegends.length }, (_, i) =>
    (min + i * step).toFixed(1)
  );
}


export function isLinearLayer(layer: Layer) {
  return layer.layer.value_type === 'CONTINUOUS';
}

export function getLayerLegends(Layer: Layer) {
  return Object.values(Layer.layer.colormap_values);
}

export function get4kmBufferExtent(lon, lat): [number, number, number, number] {
  const kmPerDegreeLat = 111; // constant
  const kmPerDegreeLon = 111 * Math.cos((lat * Math.PI) / 180);

  const latBuffer = 4 / kmPerDegreeLat;
  const lonBuffer = 4 / kmPerDegreeLon;

  return [lon - lonBuffer, lat - latBuffer, lon + lonBuffer, lat + latBuffer];
}

export function closestUpperDivisibleBy5(n: number): number {
  return n % 5 === 0 ? n : n + (5 - (n % 5));
}
export function closestUpperDivisibleBy10(n: number): number {
  return n % 10 === 0 ? n : n + (10 - (n % 10));
}

export function safeBase64Encode(utf8Bytes: Uint8Array): string {
  const CHUNK_SIZE = 0x8000;
  let binaryString = '';
  for (let i = 0; i < utf8Bytes.length; i += CHUNK_SIZE) {
    const chunk = utf8Bytes.subarray(i, i + CHUNK_SIZE);
    binaryString += String.fromCharCode.apply(null, Array.from(chunk));
  }
  return btoa(binaryString);
}

export function isNumber(value: any): value is number {
  return typeof value === 'number' && !isNaN(value);
}

export function isYear(value: number): boolean {
  return value >= 1000 && value <= 2200;
}

export function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((val, index) => deepEqual(val, b[index]));
  }

  if (typeof a === 'object' && a && b) {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => deepEqual(a[key], b[key]));
  }

  return false;
}

export function arraysEqualUnordered<T>(
  a: T[],
  b: T[],
  keyFn: (item: T) => string
): boolean {
  if (a.length !== b.length) return false;

  const mapA = new Map<string, number>();
  const mapB = new Map<string, number>();

  for (const item of a) {
    const key = keyFn(item);
    mapA.set(key, (mapA.get(key) ?? 0) + 1);
  }

  for (const item of b) {
    const key = keyFn(item);
    mapB.set(key, (mapB.get(key) ?? 0) + 1);
  }

  return deepEqual(Array.from(mapA.entries()), Array.from(mapB.entries()));
}

export function parseCaseToWords(input: string): string {
  return input
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function getValueByPath<T = unknown>(
  obj: Record<string, any>,
  path: string,
  defaultValue?: T
): T | undefined {
  if (!obj || !path) return defaultValue;

  const parts = path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean);

  let current: any = obj;

  for (const key of parts) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return defaultValue;
    }
  }

  return current ?? defaultValue;
}
